import { useCallback, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import {
  IDLE_LIMIT_MS,
  flushPendingWork,
  getLastActivityAt,
  hasActiveSession,
  markActivity,
} from "../utils/sessionActivity";

// How often the deadline is checked. The window is five hours, so a coarse
// tick is plenty and costs nothing.
const CHECK_INTERVAL_MS = 30 * 1000;

// mousemove and scroll fire continuously; the stamp only needs refreshing
// occasionally for a five-hour window.
const WRITE_THROTTLE_MS = 10 * 1000;

const ACTIVITY_EVENTS = [
  "mousedown",
  "mousemove",
  "keydown",
  "wheel",
  "touchstart",
  "touchmove",
  "scroll",
  "click",
  "input",
];

/**
 * Logs the session out after five hours with no activity, and only then —
 * anything the user does resets the clock. Work in progress is flushed
 * through the handlers registered in utils/sessionActivity before the session
 * is cleared, so nothing unsaved is lost on the way out.
 *
 * Renders nothing; mounted once at the top of the app.
 */
export default function IdleSessionGuard() {
  const history = useHistory();
  const lastWriteRef = useRef(0);
  const loggingOutRef = useRef(false);
  const hadSessionRef = useRef(hasActiveSession());

  const noteActivity = useCallback(() => {
    if (loggingOutRef.current || !hasActiveSession()) return;
    const now = Date.now();
    if (now - lastWriteRef.current < WRITE_THROTTLE_MS) return;
    lastWriteRef.current = now;
    markActivity(now);
  }, []);

  useEffect(() => {
    // A session that predates this tab (a reload, a second tab) keeps its
    // existing deadline; a session with no stamp yet starts counting now.
    if (hasActiveSession() && !getLastActivityAt()) markActivity();

    const opts = { passive: true, capture: true };
    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, noteActivity, opts));

    // Coming back to the tab counts as activity, and so does a keystroke in
    // an iframe, which the window listeners above never see.
    const onVisible = () => {
      if (document.visibilityState === "visible") noteActivity();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", noteActivity);

    return () => {
      ACTIVITY_EVENTS.forEach((evt) =>
        window.removeEventListener(evt, noteActivity, opts),
      );
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", noteActivity);
    };
  }, [noteActivity]);

  useEffect(() => {
    const timer = setInterval(async () => {
      if (loggingOutRef.current) return;

      const active = hasActiveSession();
      if (!active) {
        hadSessionRef.current = false;
        return;
      }

      // A session that has just appeared is a fresh login: start its clock
      // now, so a stamp left behind by an earlier session cannot expire it
      // the moment the user signs in.
      if (!hadSessionRef.current) {
        hadSessionRef.current = true;
        markActivity();
        return;
      }

      const last = getLastActivityAt();
      if (!last) {
        markActivity();
        return;
      }
      if (Date.now() - last < IDLE_LIMIT_MS) return;

      loggingOutRef.current = true;
      // Save first, tear down second — a handler that fails or hangs cannot
      // block the logout, it is capped inside flushPendingWork.
      await flushPendingWork();
      history.replace("/logout?reason=idle");
    }, CHECK_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [history]);

  return null;
}
